import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Box,
  Anchor,
  Alert,
} from '@mantine/core';
import { useAuth } from '../../contexts/AuthContext';
import { ErrorDisplay } from '../common/ErrorDisplay';
import {
  DisplayableError,
  errorHandler,
} from '../../services/utils/errorHandler';
import {
  validateName,
  validateUsername,
  validateEmail,
  validatePassword,
  validateDateOfBirth,
  validateSignUpForm,
  FIELD_VALIDATION_EMPTY,
  type FieldValidation,
} from '../../utils/validation/authValidation';

const SignUp: React.FC = () => {
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<DisplayableError | null>(null);
  const [loading, setLoading] = useState(false);

  const [nameValidation, setNameValidation] = useState<FieldValidation>(
    FIELD_VALIDATION_EMPTY
  );
  const [usernameValidation, setUsernameValidation] = useState<FieldValidation>(
    FIELD_VALIDATION_EMPTY
  );
  const [emailValidation, setEmailValidation] = useState<FieldValidation>(
    FIELD_VALIDATION_EMPTY
  );
  const [passwordValidation, setPasswordValidation] = useState<FieldValidation>(
    FIELD_VALIDATION_EMPTY
  );
  const [dateOfBirthValidation, setDateOfBirthValidation] =
    useState<FieldValidation>(FIELD_VALIDATION_EMPTY);

  const { signUp } = useAuth();
  const navigate = useNavigate();

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setNameValidation(value ? validateName(value) : FIELD_VALIDATION_EMPTY);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUsername(value);
    setUsernameValidation(
      value ? validateUsername(value) : FIELD_VALIDATION_EMPTY
    );
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setEmailValidation(value ? validateEmail(value) : FIELD_VALIDATION_EMPTY);
  };

  const handleDateOfBirthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDateOfBirth(value);
    setDateOfBirthValidation(
      value ? validateDateOfBirth(value) : FIELD_VALIDATION_EMPTY
    );
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordValidation(
      value ? validatePassword(value) : FIELD_VALIDATION_EMPTY
    );
  };

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateSignUpForm({
      name,
      username,
      email,
      dateOfBirth,
      password,
      confirmPassword,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    const result = await signUp(name, username, email, password, dateOfBirth);
    setLoading(false);

    if (result.success) {
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    setError(
      result.processedError ??
        (result.error
          ? errorHandler.processError(new Error(result.error))
          : null)
    );
  };

  return (
    <Container size="xs" mt="xl">
      <Box ta="center">
        <Paper shadow="md" p="xl" radius="md" w="100%">
          <Title order={1} ta="center" mb="lg">
            Sign Up
          </Title>

          {success && (
            <Alert color="green" mb="md">
              Account created successfully! Please log in with your new
              credentials.
            </Alert>
          )}

          {error && <ErrorDisplay error={error} className="mb-4" />}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextInput
              label="Name"
              placeholder="Enter your full name"
              required
              id="name"
              name="name"
              autoComplete="name"
              value={name}
              onChange={handleNameChange}
              disabled={loading}
              error={nameValidation.validateStatus === 'error'}
              description={nameValidation.errorMsg}
              mb="md"
            />
            <TextInput
              label="Username"
              placeholder="Enter your username"
              required
              id="username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={handleUsernameChange}
              disabled={loading}
              error={usernameValidation.validateStatus === 'error'}
              description={usernameValidation.errorMsg}
              mb="md"
            />
            <TextInput
              label="Email"
              placeholder="Enter your email"
              required
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              error={emailValidation.validateStatus === 'error'}
              description={emailValidation.errorMsg}
              mb="md"
            />
            <TextInput
              label="Date of birth"
              type="date"
              required
              id="dateOfBirth"
              name="dateOfBirth"
              data-cy="signup-dob"
              value={dateOfBirth}
              onChange={handleDateOfBirthChange}
              disabled={loading}
              error={dateOfBirthValidation.validateStatus === 'error'}
              description={dateOfBirthValidation.errorMsg}
              mb="md"
            />
            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              required
              id="password"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={handlePasswordChange}
              disabled={loading}
              error={passwordValidation.validateStatus === 'error'}
              description={passwordValidation.errorMsg}
              mb="md"
            />
            <PasswordInput
              label="Confirm Password"
              placeholder="Confirm your password"
              required
              id="confirmPassword"
              name="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfirmPassword(e.target.value)
              }
              disabled={loading}
              mb="xl"
            />
            <Button
              type="submit"
              data-cy="signup-submit"
              fullWidth
              variant="filled"
              size="md"
              loading={loading}
              disabled={loading}
              mb="md"
            >
              Sign Up
            </Button>
            <Box ta="center">
              <Anchor
                component={RouterLink}
                to="/login"
                size="sm"
                data-cy="signup-to-login"
              >
                {'Already have an account? Sign In'}
              </Anchor>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default SignUp;
